/**
 * Application Entry Point
 * Requirements: 4.2, 4.5, 13.1, 13.2, 13.3, 13.4, 13.5, 13.6
 * 
 * Sets up React with TanStack Query provider for data fetching
 * and AuthProvider for authentication.
 */

import '@course-authoring/i18n';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import '@course-authoring/shared-types/fonts.css';
import { queryClient } from './api';
import { AuthProvider } from './auth';
import { ClientContextProvider } from './hooks/useClientContext';
import { LanguageContextProvider } from './hooks/useLanguageContext';
import { App } from './App';
import './editor.css';

function ApplicationRoot() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClientContextProvider>
          <LanguageContextProvider>
            <App />
          </LanguageContextProvider>
        </ClientContextProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Keep a single data router for the app lifetime. App retains its existing
// descendant Routes tree; this catch-all route only supplies data-router APIs
// such as useBlocker to the opt-in Theme Settings page.
const router = createBrowserRouter([{ path: '*', element: <ApplicationRoot /> }]);

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
